import { useCallback, createContext, useContext, useRef, ReactNode, useSyncExternalStore } from "react";
import toast from "react-hot-toast/headless";

//---------------<Data types>------------------------------------------------------------------------------------------

export type NotificationsDataSource = {
	//------members portal----------------------------------
	// json base URL config
	updateLanguageNotifications?: string[];
	// login page (no need to track log out errors as the user is invalidated in every case)
	logInNotifications?: string[];
	customerConsentNotifications?: string[];
	sendForgotPassNotifications?: string[];
	// dashboard page
	getDashboardPageDataNotifications?: string[];
	getPetsListNotifications?: string[];
	dashboardPageUpdateOrderAddress?: string[];
	dashboardPageUpdateOrderItem?: string[];
	// account page
	getAccountPageDataNotifications?: string[];
	check2FApinVerifiedNotifications?: string[];
	verify2FApinNotifications?: string[];
	updateCustomerDetailsNotifications?: string[];
	changePasswordNotifications?: string[];
	send2FApinNotifications?: string[];
	updateAddressesAndReturnAddressListNotifications?: string[];
	updatePaymentMethodsAndReturnPaymentMethodsListNotifications?: string[];
	confirmPaymentMethodNotifications?: string[];
	addPaymentMethodDialogNotifications?: string[];
	// subscription page
	getSubscriptionPageDataNotifications?: string[];
	priceChangeNotifications?: string[];
	// getMore page
	getGetMorePageDataNotifications?: string[];
	// referrals page
	getReferralsPageDataNotifications?: string[];
	getReferralInvitationListNotifications?: string[];
	sendReferralInvitationNotifications?: string[];
	copyInvitationLinkNotifications?: string[];
	// support page
	getSupportPageDataNotifications?: string[];
	// education page
	getEducationPageDataNotifications?: string[];
	// products page
	getProductsPageDataNotifications?: string[];
	addOneProductToCartNotifications?: string[];
	// product details page
	getProductDetailsPageDataNotifications?: string[];
	// orders page
	getOrdersPageDataNotifications?: string[];
	cancelOrderNotifications?: string[];
	retryPaymentNotifications?: string[];
	trackOrderNotifications?: string[];
	getOrderDetailsPageDataNotifications?: string[];
	// pet form page
	getPetFormPageDataNotifications?: string[];
	addOrUpdatePetNotifications?: string[];
	deletePetNotifications?: string[];
	// cart page
	getCartPageDataNotifications?: string[];
	updateMultipleCartProductsNotifications?: string[]; // co used by treats page
	saveCartToSubscriptionNotifications?: string[];
	createAdhocOrderNotifications?: string[];
	// treats in next box page
	getTreatsInNextBoxDataNotifications?: string[];
	// content article page
	getContentArticlePageDataNotification?: string[];
	// user credit
	userCreditNotifications?: string[];
	//------checkout----------------------------------------
	checkoutPageDataNotifications?: string[];
	makePaymentNotifications?: string[];
	//------find address------------------------------------
	findAddressNotifications?: string[];
};

export type NotificationType = "success" | "error" | "loading" | "custom_user_credit" | "navigate_to_portal" | "navigate_to_nutrition_plan";

//---------------</Data types>-----------------------------------------------------------------------------------------
//---------------<Notification clearing groups>------------------------------------------------------------------------

export const clearAll_ButUserCredit: Partial<NotificationsDataSource> = {
	//------members portal----------------------------------
	updateLanguageNotifications: undefined,
	logInNotifications: undefined,
	customerConsentNotifications: undefined,
	sendForgotPassNotifications: undefined,
	getDashboardPageDataNotifications: undefined,
	getAccountPageDataNotifications: undefined,
	check2FApinVerifiedNotifications: undefined,
	verify2FApinNotifications: undefined,
	updateCustomerDetailsNotifications: undefined,
	changePasswordNotifications: undefined,
	send2FApinNotifications: undefined,
	updateAddressesAndReturnAddressListNotifications: undefined,
	updatePaymentMethodsAndReturnPaymentMethodsListNotifications: undefined,
	confirmPaymentMethodNotifications: undefined,
	addPaymentMethodDialogNotifications: undefined,
	getSubscriptionPageDataNotifications: undefined,
	priceChangeNotifications: undefined,
	getPetsListNotifications: undefined,
	dashboardPageUpdateOrderAddress: undefined,
	dashboardPageUpdateOrderItem: undefined,
	getGetMorePageDataNotifications: undefined,
	getReferralsPageDataNotifications: undefined,
	getReferralInvitationListNotifications: undefined,
	sendReferralInvitationNotifications: undefined,
	copyInvitationLinkNotifications: undefined,
	getSupportPageDataNotifications: undefined,
	getEducationPageDataNotifications: undefined,
	getProductsPageDataNotifications: undefined,
	addOneProductToCartNotifications: undefined,
	getProductDetailsPageDataNotifications: undefined,
	getOrdersPageDataNotifications: undefined,
	cancelOrderNotifications: undefined,
	retryPaymentNotifications: undefined,
	trackOrderNotifications: undefined,
	getOrderDetailsPageDataNotifications: undefined,
	getPetFormPageDataNotifications: undefined,
	addOrUpdatePetNotifications: undefined,
	deletePetNotifications: undefined,
	getCartPageDataNotifications: undefined,
	updateMultipleCartProductsNotifications: undefined,
	saveCartToSubscriptionNotifications: undefined,
	createAdhocOrderNotifications: undefined,
	getTreatsInNextBoxDataNotifications: undefined,
	getContentArticlePageDataNotification: undefined,
	// # user credit - we do not clear user credit (only on login page)
	// userCreditNotifications: undefined
	findAddressNotifications: undefined
}

export const clearAll_ButUserCredit_and_CreateAdhocOrder: Partial<NotificationsDataSource> = {
	//------members portal----------------------------------
	updateLanguageNotifications: undefined,
	logInNotifications: undefined,
	customerConsentNotifications: undefined,
	sendForgotPassNotifications: undefined,
	getDashboardPageDataNotifications: undefined,
	getAccountPageDataNotifications: undefined,
	check2FApinVerifiedNotifications: undefined,
	verify2FApinNotifications: undefined,
	updateCustomerDetailsNotifications: undefined,
	changePasswordNotifications: undefined,
	send2FApinNotifications: undefined,
	updateAddressesAndReturnAddressListNotifications: undefined,
	updatePaymentMethodsAndReturnPaymentMethodsListNotifications: undefined,
	confirmPaymentMethodNotifications: undefined,
	addPaymentMethodDialogNotifications: undefined,
	getSubscriptionPageDataNotifications: undefined,
	priceChangeNotifications: undefined,
	getPetsListNotifications: undefined,
	dashboardPageUpdateOrderAddress: undefined,
	dashboardPageUpdateOrderItem: undefined,
	getGetMorePageDataNotifications: undefined,
	getReferralsPageDataNotifications: undefined,
	getReferralInvitationListNotifications: undefined,
	sendReferralInvitationNotifications: undefined,
	copyInvitationLinkNotifications: undefined,
	getSupportPageDataNotifications: undefined,
	getEducationPageDataNotifications: undefined,
	getProductsPageDataNotifications: undefined,
	addOneProductToCartNotifications: undefined,
	getProductDetailsPageDataNotifications: undefined,
	getOrdersPageDataNotifications: undefined,
	cancelOrderNotifications: undefined,
	retryPaymentNotifications: undefined,
	trackOrderNotifications: undefined,
	getOrderDetailsPageDataNotifications: undefined,
	getPetFormPageDataNotifications: undefined,
	addOrUpdatePetNotifications: undefined,
	deletePetNotifications: undefined,
	getCartPageDataNotifications: undefined,
	updateMultipleCartProductsNotifications: undefined,
	saveCartToSubscriptionNotifications: undefined,
	// # meant for usage on the order details page
	//createAdhocOrderNotifications: undefined
	getTreatsInNextBoxDataNotifications: undefined,
	getContentArticlePageDataNotification: undefined,
	// # user credit - we do not clear user credit (only on login page)
	// userCreditNotifications: undefined
	findAddressNotifications: undefined
}

export const clearAll_ButUserCredit_and_AddToNextBox: Partial<NotificationsDataSource> = {
	//------members portal----------------------------------
	updateLanguageNotifications: undefined,
	logInNotifications: undefined,
	customerConsentNotifications: undefined,
	sendForgotPassNotifications: undefined,
	getDashboardPageDataNotifications: undefined,
	getAccountPageDataNotifications: undefined,
	check2FApinVerifiedNotifications: undefined,
	verify2FApinNotifications: undefined,
	updateCustomerDetailsNotifications: undefined,
	changePasswordNotifications: undefined,
	send2FApinNotifications: undefined,
	updateAddressesAndReturnAddressListNotifications: undefined,
	updatePaymentMethodsAndReturnPaymentMethodsListNotifications: undefined,
	confirmPaymentMethodNotifications: undefined,
	addPaymentMethodDialogNotifications: undefined,
	getSubscriptionPageDataNotifications: undefined,
	priceChangeNotifications: undefined,
	getPetsListNotifications: undefined,
	dashboardPageUpdateOrderAddress: undefined,
	dashboardPageUpdateOrderItem: undefined,
	getGetMorePageDataNotifications: undefined,
	getReferralsPageDataNotifications: undefined,
	getReferralInvitationListNotifications: undefined,
	sendReferralInvitationNotifications: undefined,
	copyInvitationLinkNotifications: undefined,
	getSupportPageDataNotifications: undefined,
	getEducationPageDataNotifications: undefined,
	getProductsPageDataNotifications: undefined,
	addOneProductToCartNotifications: undefined,
	getProductDetailsPageDataNotifications: undefined,
	getOrdersPageDataNotifications: undefined,
	cancelOrderNotifications: undefined,
	retryPaymentNotifications: undefined,
	trackOrderNotifications: undefined,
	getOrderDetailsPageDataNotifications: undefined,
	getPetFormPageDataNotifications: undefined,
	addOrUpdatePetNotifications: undefined,
	deletePetNotifications: undefined,
	getCartPageDataNotifications: undefined,
	updateMultipleCartProductsNotifications: undefined,
	// # meant for usage on: Treats in next bo page
	// saveCartToSubscriptionNotifications: undefined,
	// # meant for usage on the order details page
	// createAdhocOrderNotifications: undefined
	getTreatsInNextBoxDataNotifications: undefined,
	getContentArticlePageDataNotification: undefined,
	// # user credit - we do not clear user credit (only on login page)
	// userCreditNotifications: undefined
	findAddressNotifications: undefined
}

export const checkout_clearAll: Partial<NotificationsDataSource> = {
	//------checkout----------------------------------------
	checkoutPageDataNotifications: undefined,
	makePaymentNotifications: undefined,
	findAddressNotifications: undefined
}

//---------------</Notification clearing groups>----------------------------------------------------------------------
//---------------<Notifications data context>--------------------------------------------------------------------------

function useNotificationsDataStore(): {
	get: () => NotificationsDataSource;
	set: (value: Partial<NotificationsDataSource>, notificationType?: NotificationType) => void; // so we can set individual properties of the object
	subscribe: (callback: () => void) => () => void;
} {
	// the init value is set inside, because the auth context should only initialize 1x
	// this adds to simplicity of use and there is no benefit to exposing it as a property
	const store = useRef<NotificationsDataSource>({});

	// we use set, because we only want a single type of a function being added once
	const subscribers = useRef(new Set<()=> void>()); 

	const get = useCallback(() => store.current, []);
	const set = useCallback((value: Partial<NotificationsDataSource>, notificationType?: NotificationType) => {
		//----------<handle notifications>------------------------------------------------
		const valueKeys = Object.keys(value);
		for (const valueKey of valueKeys) {
			// dismiss any possible previous error notifications for this error
			const previousValues = store.current[valueKey as keyof NotificationsDataSource];
			if (previousValues) {
				for (const previousID of previousValues) {
					if (previousID)
						toast.dismiss(previousID);
				}
			}
			// add new error notifications and save their toast IDs
			let newValues = value[valueKey as keyof NotificationsDataSource];
			if (newValues) {
				for (const i in newValues) {
					let toastID: string | undefined = undefined;
					switch (notificationType) {
						case "success":
							toastID = toast.success(newValues[i]);
							break;
						case "error":
							toastID = toast.error(newValues[i]);
							break;
						/* NOTE:
						We will use className in toasts for identification of notification subtypes.
						Although it WILL NOT appear on custom components which we use for every notification, it would appear on default toasts.
						Because of this we should still never use a name that is the same to any CSS class name.
						
						Good way to ensure this is to use only: 
						> kebab case for CSS classes: "some-class-name"
						> underscore case for NotificationType "some_type_name"
						*/
						case "loading":
							toastID = toast.custom(newValues[i], { className: "loading" });
							break;
						case "custom_user_credit":
							toastID = toast.custom(newValues[i], { className: "custom_user_credit" });
							break;
						case "navigate_to_portal":
							toastID = toast.custom(newValues[i], { className: "navigate_to_portal" });
							break;
						case "navigate_to_nutrition_plan":
							toastID = toast.custom(newValues[i], { className: "navigate_to_nutrition_plan" });
							break;
						default:
							toastID = toast.error(newValues[i]);
					}

					newValues[i] = toastID;
				}
				value[valueKey as keyof NotificationsDataSource] = newValues;
			}
		}
		//----------</handle notifications>-----------------------------------------------

		store.current = { ...store.current, ...value};
		subscribers.current.forEach((callback) => callback());
	} ,[]);

	// subscriber is added so we can subscribe to any value changes to the state (in our case of the "user") with useSyncExternalStore
	// when we add a subscriber we return the delete function of that subscriber
	const subscribe = useCallback((callback: () => void) => {
		subscribers.current.add(callback);
		return () => subscribers.current.delete(callback);
	} ,[]);

	return { get, set, subscribe };
}

type UseNotificationsDataStoreReturnType = ReturnType<typeof useNotificationsDataStore>;

const NotificationsContext = createContext<UseNotificationsDataStoreReturnType | null>(null);

export function NotificationsContextProvider({ children }: { children: ReactNode }) {
	const notificationsData = useNotificationsDataStore();
	return (
		<NotificationsContext.Provider value={notificationsData}>
			{children}
		</NotificationsContext.Provider>
	);
}

// SelectorOutput will catch the type of the selected value
// selector will rerender only components that register it
export function useNotificationsData<SelectorOutput>(
	selector: (user: NotificationsDataSource) => SelectorOutput 
): [ SelectorOutput, (value: Partial<NotificationsDataSource>, notificationType?: NotificationType) => void] {
	const notificationsContext = useContext(NotificationsContext);
	if (!notificationsContext) {
		throw new Error("NotificationsContext not found!");
	}
	const state = useSyncExternalStore(notificationsContext.subscribe, () => selector(notificationsContext.get()));
	return [state, notificationsContext.set];
}

//---------------</Notifications data context>-------------------------------------------------------------------------
